/**
 * ACIP-recommended vaccine catalog and a deliberately narrow "not yet on file" gap list —
 * `BACKLOG.md` M8's explicit instruction: **adopt Deloitte's/HLN's open-source ICE (Immunization
 * Calculation Engine) rather than hand-building real ACIP dose-interval/forecasting logic.**
 *
 * **Why this module does not implement real forecasting, and what it does instead.** ICE
 * (`https://cdsframework.atlassian.net/wiki/display/ICE/Home`, current maintainer HLN Consulting,
 * confirmed live during this build) is a Java service running inside OpenCDS, distributed as a
 * Docker image or WAR file and integrated through a standards-based web-service call — not an npm
 * package, not a library this Node/TypeScript codebase can `import`. Standing up a second, separate
 * Java/Docker service purely to forecast doses is a real integration project (its own deploy target,
 * its own HL7v2-shaped request/response mapping, its own operational surface) — not something to
 * attempt inside this single pass on a demo-scale build, and explicitly not something to route around
 * by hand-rolling a competing dose-interval/contraindication/catch-up engine, which is exactly the
 * kind of "easy to get subtly wrong" clinical logic ICE exists to centralize. `BACKLOG.md` M8's own
 * row records this as the biggest flagged execution risk in the whole module and recommends a real
 * ICE integration as a future, partnered effort — not attempted here.
 *
 * **What this module builds instead**: the honest, narrow slice the task brief asked for — a
 * reference catalog of ACIP-recommended routine childhood/adolescent vaccines (antigen name plus a
 * rough "typically recommended by this age" marker, not a full dose-count/interval schedule), and a
 * pure function that compares it against a patient's already-administered `immunizations` (the
 * existing core `clinical.ts` table this module reads but never modifies) to produce a plain "not yet
 * recorded for this age" list. This is a simple age/antigen-presence check, **not** ACIP forecasting:
 * it says nothing about dose spacing, minimum intervals, catch-up schedules, or contraindications,
 * and `missingRecommendedVaccines`'s own doc comment restates this so a caller cannot mistake its
 * output for a clinical recommendation engine.
 *
 * **Antigen list and age markers are UNREVIEWED** in the sense `growthCharts.ts`'s header uses the
 * term: the specific ages below are a reasonable approximation of the routine childhood schedule, not
 * independently checked against the ACIP's current published schedule
 * (`https://www.cdc.gov/vaccines/schedules/hcp/imz/child-adolescent.html`) during this build. Verify
 * against that source, or a real ICE integration, before treating this list as authoritative.
 */

export const VACCINE_ANTIGENS = [
  'hepatitis_b',
  'rotavirus',
  'dtap',
  'hib',
  'pneumococcal_conjugate',
  'inactivated_poliovirus',
  'influenza',
  'mmr',
  'varicella',
  'hepatitis_a',
  'tdap',
  'hpv',
  'meningococcal_acwy',
] as const;
export type VaccineAntigen = (typeof VACCINE_ANTIGENS)[number];

export function isVaccineAntigen(value: string): value is VaccineAntigen {
  return (VACCINE_ANTIGENS as readonly string[]).includes(value);
}

export interface VaccineCatalogEntry {
  readonly antigen: VaccineAntigen;
  readonly label: string;
  /** Age in days by which this antigen's *first* dose is routinely recommended to have started —
   * not a full dose-count/interval schedule, see this module's header. */
  readonly recommendedByAgeDays: number;
}

const DAYS_PER_MONTH = 30.4375;
const DAYS_PER_YEAR = 365.25;
function atMonths(months: number): number {
  return Math.round(months * DAYS_PER_MONTH);
}
function atYears(years: number): number {
  return Math.round(years * DAYS_PER_YEAR);
}

/** UNREVIEWED — see this module's header. */
export const VACCINE_CATALOG: readonly VaccineCatalogEntry[] = [
  { antigen: 'hepatitis_b', label: 'Hepatitis B', recommendedByAgeDays: atMonths(1) },
  { antigen: 'rotavirus', label: 'Rotavirus', recommendedByAgeDays: atMonths(2) },
  { antigen: 'dtap', label: 'DTaP', recommendedByAgeDays: atMonths(2) },
  { antigen: 'hib', label: 'Hib', recommendedByAgeDays: atMonths(2) },
  {
    antigen: 'pneumococcal_conjugate',
    label: 'Pneumococcal conjugate (PCV)',
    recommendedByAgeDays: atMonths(2),
  },
  {
    antigen: 'inactivated_poliovirus',
    label: 'Inactivated poliovirus (IPV)',
    recommendedByAgeDays: atMonths(2),
  },
  { antigen: 'influenza', label: 'Influenza (annual)', recommendedByAgeDays: atMonths(6) },
  { antigen: 'mmr', label: 'MMR', recommendedByAgeDays: atMonths(12) },
  { antigen: 'varicella', label: 'Varicella', recommendedByAgeDays: atMonths(12) },
  { antigen: 'hepatitis_a', label: 'Hepatitis A', recommendedByAgeDays: atMonths(12) },
  { antigen: 'tdap', label: 'Tdap booster', recommendedByAgeDays: atYears(11) },
  { antigen: 'hpv', label: 'HPV', recommendedByAgeDays: atYears(11) },
  {
    antigen: 'meningococcal_acwy',
    label: 'Meningococcal ACWY',
    recommendedByAgeDays: atYears(11),
  },
];

export interface AdministeredVaccineRecord {
  /** Free text, matching the existing `immunizations.vaccine` column this module reads but does not
   * own — matched against `VACCINE_CATALOG` by substring/keyword rather than requiring the recorded
   * text to equal an antigen id exactly, since real-world entries name a product ("RotaTeq"), not
   * the antigen. `matchesAntigen` below is the one place that matching happens. */
  readonly vaccine: string;
}

/** Loose, case-insensitive keyword match between a recorded vaccine name and a catalog antigen — not
 * a real terminology mapping (no CVX/RxNorm code lookup), an honest limitation for a v1 gap list. */
function matchesAntigen(recordedVaccine: string, antigen: VaccineCatalogEntry): boolean {
  const normalized = recordedVaccine.toLowerCase();
  const keywords: Record<VaccineAntigen, readonly string[]> = {
    hepatitis_b: ['hepatitis b', 'hep b', 'hepb'],
    rotavirus: ['rotavirus', 'rotateq', 'rotarix'],
    dtap: ['dtap', 'diphtheria'],
    hib: ['hib', 'haemophilus'],
    pneumococcal_conjugate: ['pneumococcal', 'pcv', 'prevnar'],
    inactivated_poliovirus: ['polio', 'ipv'],
    influenza: ['influenza', 'flu'],
    mmr: ['mmr', 'measles'],
    varicella: ['varicella', 'chickenpox'],
    hepatitis_a: ['hepatitis a', 'hep a', 'hepa'],
    tdap: ['tdap', 'tetanus'],
    hpv: ['hpv', 'papillomavirus', 'gardasil'],
    meningococcal_acwy: ['meningococcal', 'menactra', 'menveo'],
  };
  return keywords[antigen.antigen].some((keyword) => normalized.includes(keyword));
}

/**
 * Which `VACCINE_CATALOG` antigens have **no** matching entry in `administered` and whose recommended
 * age has already arrived for a patient of `patientAgeDays`. This is a plain presence check against a
 * fixed age marker — **not** ACIP dose-interval/catch-up/contraindication forecasting; see this
 * module's header for why that logic is deliberately not built here. A caller-facing label should say
 * "not yet recorded" or "may be due," never "overdue" or anything implying a clinical determination
 * this function cannot make.
 */
export function missingRecommendedVaccines(
  administered: readonly AdministeredVaccineRecord[],
  patientAgeDays: number,
): readonly VaccineCatalogEntry[] {
  return VACCINE_CATALOG.filter((entry) => {
    if (entry.recommendedByAgeDays > patientAgeDays) return false;
    return !administered.some((record) => matchesAntigen(record.vaccine, entry));
  });
}
